import { useState, useEffect } from "react";
import { Modal } from "reactstrap";
import { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from "react-joyride";

import SectionHeader from "../section-header";
import Button from "../button";

import GrapeJsEditor from "../grapejs-editor";
import GuidedTour from "../guided-tour";
import { tourGuideCheck } from "../../utils/functions";

type Props = {
  isOpen: boolean;
  close: any;
  msgContent: string;
  businessSlug: string;
  setMsgContent: React.Dispatch<React.SetStateAction<string>>;
  setSaveEditedTemplate: React.Dispatch<React.SetStateAction<boolean>>;
};

const editImageSteps: Step[] = [
  {
    // ← Update this to match the selector of your template’s <img>
    // target: "img#email-image",
    target: ".gjs-frames",
    content:
      "To change the hero image on this campaign: Double-click the hero image below.",
    placement: "top",
    disableBeacon: true,
  },
  {
    // The  Asset Manager pop-up
    // find the button by its exact innerText
    // target: (() =>
    //   Array.from(document.querySelectorAll("button")).find(
    //     (el) => el.textContent?.trim() === "Upload"
    //   ) as HTMLElement)(),
    // target: '.gjs-am-file-uploader input[type="file"]',
    target: ".gjs-am-custom-wrp",
    content: "Click here to upload a new image",
    placement: "top",
  },
  {
    // The “Select” button overlay on the uploaded thumb
    target: ".gjs-am-assets .gjs-am-btn--select",
    content: "Now click “Select” to apply it to your template",
    placement: "top",
  },
  {
    // Back to the canvas image after update
    // target: "img#email-image",
    target: ".gjs-frames",
    content: "Great! Your image has been updated in the template.",
    placement: "bottom",
  },
];

const EditorModal = ({
  isOpen,
  close,
  msgContent,
  businessSlug,
  setMsgContent,
  setSaveEditedTemplate,
}: Props) => {
  const [grapejsEditor, setGrapejsEditor] = useState<any>();
  const [runEditorTour, setRunEditorTour] = useState(false);
  const [editorTourStepIndex, setEditorTourStepIndex] = useState(0);
  const [assetOpen, setAssetOpen] = useState(false);

  const onReady = (grapejsEditor: any) => setGrapejsEditor(grapejsEditor);

  useEffect(() => {
    if (grapejsEditor) {
      tourGuideCheck("editImageTourCompleted", setRunEditorTour);
    }
  }, [grapejsEditor]);

  // Listen for Asset Manager events
  useEffect(() => {
    if (!grapejsEditor) return;

    const bump = (current: number) =>
      current === editorTourStepIndex && setEditorTourStepIndex(current + 1);

    grapejsEditor.on("asset:open", () => {
      console.log("fire open assets event!");
      setAssetOpen(true);
      bump(0);
    });
    grapejsEditor.on("asset:upload:end", () => bump(1));
    grapejsEditor.on("asset:close", () => bump(2));

    return () => {
      grapejsEditor.off("asset:open");
      grapejsEditor.off("asset:upload:end");
      grapejsEditor.off("asset:close");
    };
  }, [grapejsEditor, editorTourStepIndex]);

  const handleEditorTourCallback = (data: CallBackProps) => {
    const { action, type, index, status } = data;
    console.log(
      `action : ${action}`,
      `index: ${index}`,
      `status: ${status}`,
      `type: ${type}`
    );

    // If they hit Next on step 0 before the AM is open, open it for them
    if (
      type === EVENTS.STEP_AFTER &&
      action === ACTIONS.NEXT &&
      index === 0 &&
      !assetOpen
    ) {
      console.log("open assets here!");
      grapejsEditor.runCommand("open-assets");
      return;
    }

    // Next button
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      setEditorTourStepIndex(index + 1);
    }
    // Back button
    else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      setEditorTourStepIndex(index - 1);
    }

    // Finish or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setEditorTourStepIndex(0);
      localStorage.setItem("editImageTourCompleted", "true");
    }
  };

  const saveMessageChanges = () => {
    if (grapejsEditor) {
      console.log("Grapejs html:", grapejsEditor.getHtml());
      setMsgContent(grapejsEditor.getHtml());
      setSaveEditedTemplate(true);
      close();
    }
  };

  return (
    <Modal className="editor_modal template_large" isOpen={isOpen}>
      <GuidedTour
        runTour={runEditorTour}
        setRunTour={setRunEditorTour}
        steps={editImageSteps}
        tourStepIndex={editorTourStepIndex}
        setTourStepIndex={setEditorTourStepIndex}
        tourCallBack={handleEditorTourCallback}
      />
      <div className="modal_header">
        <SectionHeader
          leftSide={<div className="title_box">EDIT CAMPAIGN MESSAGE</div>}
          rightSide={
            <>
              <div className="icon" onClick={close}>
                {/* <Icon name="close" /> */}
                <Button
                  className="btn_secondary"
                  text="Close"
                  onClick={close}
                />
                <Button
                  text="Save Changes"
                  btnStyle={{ marginLeft: "1rem" }}
                  onClick={saveMessageChanges}
                />
              </div>
            </>
          }
        />
      </div>

      <div className="modal_body">
        <div className="editor_container">
          <GrapeJsEditor
            content={msgContent}
            onReady={onReady}
            businessSlug={businessSlug}
          />
        </div>
        <div className="btn-box">
          <Button className="btn_secondary" text="Close" onClick={close} />
          <Button text="Save Changes" onClick={saveMessageChanges} />
        </div>
      </div>
    </Modal>
  );
};
export default EditorModal;
